/**
 * MobileReportAccordion - Collapsible audit report sections for mobile.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/v2/ui/Accordion';
import type { ReactNode } from 'react';

interface SectionDef {
  id: string;
  labelKey: string;
  content: unknown;
  node: ReactNode;
}

interface MobileReportAccordionProps {
  sections: SectionDef[];
}

export function MobileReportAccordion({ sections }: MobileReportAccordionProps) {
  const { t } = useTranslation();
  const allIds = sections.map((s) => s.id);
  const [openIds, setOpenIds] = useState<string[]>([allIds[0] ?? '']);

  const allOpen = openIds.length === allIds.length;

  return (
    <div className="space-y-2">
      <button
        onClick={() => setOpenIds(allOpen ? [] : allIds)}
        className="text-xs font-medium text-primary-600 dark:text-primary-400 px-1 py-1 hover:underline"
      >
        {allOpen
          ? t('amazonAudit.accordion.collapseAll', { defaultValue: 'Alle schließen' })
          : t('amazonAudit.accordion.expandAll', { defaultValue: 'Alle öffnen' })}
      </button>

      <Accordion type="multiple" value={openIds} onValueChange={setOpenIds}>
        {sections.map((s) => (
          <AccordionItem key={s.id} value={s.id}>
            <AccordionTrigger className="text-sm font-semibold">
              {t(s.labelKey, { defaultValue: formatLabel(s.id) })}
            </AccordionTrigger>
            <AccordionContent>
              <div className="pt-2">{s.node}</div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function formatLabel(id: string): string {
  return id
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
